import { parseISO } from "date-fns";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { EmptyState } from "~/components/empty-state";
import { Badge, Button } from "~/components/ui";
import type { EvidenceVersionRow } from "~/features/evidences/types";
import { buildEvidenceVersionDiffs } from "~/features/evidences/utils/versionDiff";
import { cn } from "~/lib/utils";

import { EvidenceVersionDiff } from "./EvidenceVersionDiff";

const OPERATION_KEY: Record<string, string> = {
  create: "ui.create",
  update: "ui.update",
  deactivate: "ui.deactivate",
};

interface EvidenceVersionHistoryTabProps {
  headVersion: number | null;
  versions: EvidenceVersionRow[];
  selectedVersionId: string | null;
}

/** Version history for a backfilled evidence — one row per snapshot, newest first. */
export function EvidenceVersionHistoryTab({
  headVersion,
  versions,
  selectedVersionId,
}: EvidenceVersionHistoryTabProps) {
  const { t } = useTranslation();
  const diffsByVersionId = useMemo(() => buildEvidenceVersionDiffs(versions), [versions]);
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(
    () => new Set(selectedVersionId ? [selectedVersionId] : []),
  );
  const [prevSelectedVersionId, setPrevSelectedVersionId] = useState(selectedVersionId);

  // Open a newly selected row during render, not in an effect, so it never paints closed first.
  if (selectedVersionId !== prevSelectedVersionId) {
    setPrevSelectedVersionId(selectedVersionId);
    if (selectedVersionId) setOpenIds((prev) => new Set([...prev, selectedVersionId]));
  }

  const toggle = (id: string) =>
    setOpenIds((prev) => (prev.has(id) ? new Set([...prev].filter((x) => x !== id)) : new Set([...prev, id])));

  if (versions.length === 0) {
    return <EmptyState title={t("ui.versionHistory")} description={t("feature.common.noData")} />;
  }

  return (
    <ul className="space-y-2">
      {versions.map((v) => {
        const isOpen = openIds.has(v.id);
        const diffId = `evidence-version-diff-${v.id}`;
        return (
          <li
            key={v.id}
            aria-current={v.id === selectedVersionId ? "true" : undefined}
            className={cn(
              "rounded-[var(--radius-md)] border px-4 py-3 text-[13px]",
              v.id === selectedVersionId ? "border-primary-500 bg-primary-50" : "border-border-default bg-white",
            )}
          >
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                aria-label={t("ui.diff")}
                aria-expanded={isOpen}
                aria-controls={isOpen ? diffId : undefined}
                onClick={() => toggle(v.id)}
              >
                <ChevronDown className={cn("transition-transform", isOpen && "rotate-180")} />
              </Button>
              <Badge tone={v.version === headVersion ? "primary" : "default"}>
                {t("ui.version")} {v.version}
              </Badge>
              <span className="text-fg-2">{t(OPERATION_KEY[v.operation] ?? "ui.unknown")}</span>
              <span className="text-fg-3">{t("date.long", { date: parseISO(v.createdAt) })}</span>
              {v.isDeactivated && <Badge tone="default">{t("ui.deactivated")}</Badge>}
              {v.page && (
                <span className="text-fg-3">
                  {t("model.evidence.citation")}: {v.page}
                </span>
              )}
              {v.referenceId && (
                <Link
                  to={`/references/${v.referenceId}`}
                  aria-label={t("ui.detail")}
                  className="ml-auto shrink-0 text-fg-link"
                >
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              )}
            </div>
            {isOpen && (
              <div className="mt-3">
                <EvidenceVersionDiff id={diffId} diffs={diffsByVersionId[v.id] ?? []} />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
